import {
	AfterViewInit,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	OnDestroy,
	OnInit,
	WritableSignal,
	inject,
	signal
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Capacitor, PluginListenerHandle, SystemBars, SystemBarType } from "@capacitor/core";
import { Device } from "@capacitor/device";
import { Keyboard } from "@capacitor/keyboard";
import { Network } from "@capacitor/network";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Platform } from "@ionic/angular";
import { IonApp, IonIcon, IonProgressBar, IonRouterOutlet } from "@ionic/angular/standalone";
import { Store } from "@ngrx/store";
import { TranslatePipe } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter, take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { GetScanning } from "./shared/actions/utility.actions";
import { IEvent, IEventUser } from "./shared/interfaces";
import { getContainersState } from "./shared/selectors/containers.selectors";
import { getLoading, getOfflineNoData, getScanning } from "./shared/selectors/utility.selectors";
import {
	AuthService,
	ContainersService,
	DeviceInfoService,
	LanguagesService,
	NavigationServices,
	StorageService,
	UtilityService
} from "./shared/services";
import { NotificationsService } from "./shared/services/notifications.service";
import { OtaService } from "./shared/services/ota.service";
import { getCurrentEvent } from "./shared/selectors/events.selectors";
import { TextZoom } from "@capacitor/text-zoom";
import { SafeHtmlPipe } from "./shared/pipes/safe-html";
// import { register } from "swiper/element/bundle";
// register();

@Component({
	selector: "app-root",
	templateUrl: "app.component.html",
	styleUrls: ["app.component.scss"],
	imports: [IonApp, IonRouterOutlet, IonIcon, IonProgressBar, TranslatePipe, SafeHtmlPipe],
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
	private SLanguages = inject(LanguagesService);
	private SContainers = inject(ContainersService);
	private store = inject(Store);
	private SAuth = inject(AuthService);
	private SNotifications = inject(NotificationsService);
	private SStorage = inject(StorageService);
	platform = inject(Platform);
	private deviceInfoService = inject(DeviceInfoService);
	private SNavigation = inject(NavigationServices);
	private readonly ota = inject(OtaService);

	public SUtility = inject(UtilityService);
	private router = inject(Router);

	inApp: boolean = false;
	eventUser: IEventUser;

	subscriptions: Subscription[] = [];
	onPauseSubscription: Subscription;
	onResumeSubscription: Subscription;
	private keyboardShowHandle: PluginListenerHandle | null = null;
	private keyboardHideHandle: PluginListenerHandle | null = null;
	loader: WritableSignal<boolean> = signal(true);

	isMobile: boolean;
	scanning: boolean = false;

	currentEvent: IEvent;

	onMaintenanceApp: boolean = false;
	logoPlatform =
		environment.firebase.projectId === "invent-app-prd"
			? "/assets/images/invent/logo.png"
			: "/assets/images/b3/logo.png";

	constructor() {
		this.isMobile = this.platform.is("ios") || this.platform.is("android");
		this.initOnApp();
	}

	async initOnApp() {
		try {
			// S'assurer que le splashscreen natif est bien affiché dès le premier instant.
			// Sur Android, l'API Android 12+ peut bloquer un show() si isVisible est resté actif sur un splash blanc.
			// On réinitialise proprement avec SplashScreen.hide() puis SplashScreen.show({ autoHide: false, fadeInDuration: 0 }).
			if (this.platform.is("android")) {
				try {
					await SplashScreen.show({
						autoHide: false,
						fadeInDuration: 0
					});
				} catch (error) {
					console.error("Error ensuring Android splashscreen:", error);
				}
			} else if (this.platform.is("ios")) {
				try {
					await SplashScreen.show({
						autoHide: false
					});
				} catch (error) {
					console.error("Error ensuring iOS splashscreen:", error);
				}
			}

			// Initialize StatusBar for Android edge-to-edge
			if (this.platform.is("android")) {
				try {
					await StatusBar.setBackgroundColor({ color: "#00000000" });
					await StatusBar.setOverlaysWebView({ overlay: true });
					await StatusBar.setStyle({ style: Style.Light });
				} catch (error) {
					console.error("Error configuring StatusBar:", error);
				}
			}

			this.resetTextZoomConfig();
			this.manageAppPauseResume();

			this.deviceInfoService.getPlatformInfo();

			if (this.platform.is("mobile") && window.innerWidth < 768 && !this.platform.is("mobileweb")) {
				// window.screen.orientation.lock("portrait");
				// ScreenOrientation.lock({ orientation: "portrait" });
			}
			this.store.select(getScanning).subscribe((scanning) => {
				this.scanning = scanning;

				if (scanning) {
					document.body.style.setProperty(`background-color`, "transparent !important");
				} else {
					document.body.style.setProperty(`background-color`, "inherit");
				}
			});
			this.subscriptions.push(
				this.SUtility.getAppSettings().subscribe({
					next: (settings) => {
						this.onMaintenanceApp =
							settings?.onMaintenanceApp === undefined ? false : settings.onMaintenanceApp;
					},
					error: (error) => {
						console.error("Err: ", error);
					}
				})
			);
			this.SLanguages.useAppLanguage();
			this.SUtility.listenToNetworkStatus();
			this.SUtility.manageFavicon();

			this.initApp();
		} catch (error) {
			console.error("❌ [APP] Error in initOnApp:", error);
		}
	}

	ngOnInit() {
		this.SStorage.init();

		// Toggle a body class while the native keyboard is open so global fixes (e.g. neutralizing ion-tabs padding) can apply.
		if (Capacitor.isNativePlatform()) {
			Keyboard.addListener("keyboardWillShow", () => document.body.classList.add("keyboard-open")).then(
				(handle) => (this.keyboardShowHandle = handle)
			);
			Keyboard.addListener("keyboardWillHide", () => document.body.classList.remove("keyboard-open")).then(
				(handle) => (this.keyboardHideHandle = handle)
			);
		}

		// Get container
		const containerId =
			!environment.platform.containerId || environment.platform.containerId === "defaultContainer"
				? "defaultContainer"
				: environment.platform.containerId;

		this.SContainers.getContainers(containerId);

		this.getContainer();

		// Init auth
		this.SAuth.initAuth();
		// Init my event user
		// this.SEventUsers.initMyEventUser();

		// init notif config
		if (this.isMobile && !this.platform.is("mobileweb")) {
			this.SNotifications.initOneSignal(environment.platform.oneSignalAppId);
		}

		this.subscriptions.push(
			this.store.select(getLoading).subscribe((loading) => {
				this.loader.set(loading);
			})
		);

		this.subscriptions.push(
			this.store.select(getCurrentEvent).subscribe((event) => {
				if (!this.currentEvent || (this.currentEvent && event && this.currentEvent.uid !== event.uid)) {
					this.currentEvent = event;
					this.logo =
						this.currentEvent && this.currentEvent.identity && this.currentEvent.identity.logo
							? this.currentEvent.identity.logo
							: "";
					this.initProgressBar();
				}
			})
		);

		// Navigate to offline page when offline with no cached data
		this.subscriptions.push(
			this.store.select(getOfflineNoData).subscribe((offlineNoData) => {
				if (offlineNoData) {
					this.router.navigate(["/offline"], { replaceUrl: true });
				}
			})
		);

		// Init OTA en dernier, non-bloquant
		this.ota.initialize().catch((e) => console.warn("[OTA] Init failed", e));
	}

	ngAfterViewInit() {
		if (navigator.appVersion.indexOf("Win") != -1) {
			const elements = document.querySelectorAll(".windows_scrollbar");

			elements.forEach((e) => {
				e.setAttribute("class", "custom_scrollbar");
			});
		}
	}

	ngOnDestroy() {
		this.keyboardShowHandle?.remove();
		this.keyboardHideHandle?.remove();
		this.subscriptions
			.concat(this.onPauseSubscription, this.onResumeSubscription)
			.forEach((sub) => sub?.unsubscribe());
		//this.SPresence.setPresence("offline");
	}

	progressBar: boolean = true;
	initProgress: number = 0;
	progressBarSub: Subscription;
	logo: string = "";
	initProgressBar() {
		this.progressBar = false;
	}

	manageAppPauseResume() {
		this.platform.ready().then(() => {
			if ((this.platform.is("mobile") && window.innerWidth < 768) || this.platform.is("mobileweb")) {
				this.onPauseSubscription = this.platform.pause.subscribe(() => {
					// App get in background — iOS may dismiss keyboard without firing keyboardWillHide, so clear the flag defensively.
					document.body.classList.remove("keyboard-open");
					this.resetTextZoomConfig();
				});

				this.onResumeSubscription = this.platform.resume.subscribe(() => {
					// if (this.platform.is("android")) {
					// 	this.deviceInfoService.showNavAndStatusBar();
					// }
					// App get in foreground
					document.body.classList.remove("keyboard-open");
					this.resetTextZoomConfig();
					if (this.isMobile && !this.platform.is("mobileweb")) {
						void this.SNotifications.syncPushSubscriptionFromStore();
					}
				});
			}
		});
	}

	initApp() {
		// Écoute de navigation : masquer le splashscreen quand la première page cible est prête
		this.subscriptions.push(
			this.router.events
				.pipe(
					filter((event): event is NavigationEnd => event instanceof NavigationEnd),
					filter((event) => {
						const url = (event.urlAfterRedirects || event.url).split("?")[0];
						return url !== "/" && url !== "";
					}),
					take(1)
				)
				.subscribe(() => {
					// Masquer dès le 1er NavigationEnd (login, liste ou event).
					// Les widgets gardent leur loader interne ; on n'attend plus leur layout.
					setTimeout(() => {
						this.manageSplashscreen("hide");
					}, 300);
				})
		);

		// Fallback de sécurité : masquer le splashscreen au bout de 5 secondes max si non masqué
		setTimeout(() => {
			this.manageSplashscreen("hide");
		}, 5000);
	}

	async resetTextZoomConfig() {
		if (
			this.platform.is("ios") ||
			this.platform.is("android") ||
			this.platform.is("mobileweb") ||
			this.platform.is("mobile") ||
			this.platform.is("capacitor")
		) {
			try {
				await this.platform.ready();
				if (
					this.platform.is("android") ||
					this.platform.is("ios") ||
					this.platform.is("mobileweb") ||
					this.platform.is("mobile") ||
					this.platform.is("capacitor")
				) {
					await TextZoom.set({
						value: 1.0
					});
					document.body.style.setProperty(`--ion-dynamic-font`, "initial !important");
				}
			} catch (error) {
				console.error("🚀 initTextZoomConfig error =>", error);
			}
		}
	}

	stopScan() {
		this.store.dispatch(GetScanning({ payload: false }));

		// Restore system navigation bar after barcode scan
		if (this.platform.is("android")) {
			try {
				void SystemBars.show({ bar: SystemBarType.NavigationBar });
			} catch (error) {
				console.error("Error restoring NavigationBar after scan:", error);
			}
		}
	}

	/**
	 * Container and init style
	 */
	getContainer() {
		this.subscriptions.push(
			this.store.select(getContainersState).subscribe((containersState) => {
				const container =
					!environment.platform.containerId || !containersState.currentContainer
						? containersState.defaultContainer
						: containersState.currentContainer;
				if (container) {
					/**
					 * Setting default color
					 */
					document.body.style.setProperty(`--ion-color-default`, container.ionicColors.defaultColor);
					document.body.style.setProperty(
						`--ion-color-default-contrast`,
						container.ionicColors.defaultContrastColor
					);
					document.body.style.setProperty(
						`--ion-color-default-shade`,
						container.ionicColors.defaultShadeColor
					);
					document.body.style.setProperty(`--ion-color-default-tint`, container.ionicColors.defaultTintColor);

					/**
					 * Setting colors of login
					 */

					document.body.style.setProperty(`--valid-btn-bg-color`, container.loginColors.validBtnBgColor);
					document.body.style.setProperty(`--valid-btn-color`, container.loginColors.validBtnColor);
					document.body.style.setProperty(`--login-btn-bg-color`, container.loginColors.loginBtnBgColor);
					document.body.style.setProperty(`--login-btn-color`, container.loginColors.loginBtnColor);
					document.body.style.setProperty(
						`--btn-bg-color-hover`,
						`${container.loginColors.validBtnBgColor}d9`
					);
					document.body.style.setProperty(
						`--text-color-1st-login`,
						container.loginColors.loginTextFirstColor
					);

					document.body.style.setProperty(
						`--text-color-2nd-login-desktop`,
						container.loginColors.loginTextSecondColorDesktop
					);
					document.body.style.setProperty(
						`--text-color-2nd-login-mobile`,
						container.loginColors.loginTextSecondColorMobile
					);
					document.body.style.setProperty(`--label-login-color`, container.loginColors.loginTextColor);

					/**
					 * Menu color
					 */
					document.body.style.setProperty(`--menu-background-color`, container.loginColors.loginTextColor);
					document.body.style.setProperty(`--menu-text-color`, container.loginColors.loginTextColor);
					if (navigator.appVersion.indexOf("Win") != -1) {
						if (!document.getElementById("id-custom-scrollbar")) {
							const sheet = document.createElement("style");
							sheet.id = "id-custom-scrollbar";
							sheet.innerHTML =
								// eslint-disable-next-line max-len
								".custom-scrollbar {--offset-bottom: auto !important;--overflow: hidden;overflow: auto;&::-webkit-scrollbar {width: 8px;-ms-overflow-style: none;scrollbar-width: none;scroll-behavior: smooth;}&::-webkit-scrollbar-thumb {background-color: #00000065 !important;border-radius: 12px !important;}}";
							document.body.appendChild(sheet);
						}
					}
				}
				this.loader.set(false);
			})
		);
	}

	/**
	 * Manage splashscreen
	 * @param type
	 */
	async manageSplashscreen(type: string) {
		await this.SUtility.manageSplashscreen(type as "show" | "hide");
	}
}
